import metadataJson from '../../blogs/metadata.json';

type SupportedLocale = 'en' | 'zh-TW' | 'zh-CN';

type PostLocaleData = {
  title: string;
  excerpt: string;
};

export type BlogPostMetadata = {
  slug: string;
  date: string;
  tags: string[];
  coverImage: string;
  featured: boolean;
  locales: Record<SupportedLocale, PostLocaleData>;
};

export type BlogPost = {
  slug: string;
  date: string;
  tags: string[];
  coverImage: string;
  featured: boolean;
  title: string;
  excerpt: string;
};

type MetadataFile = {
  posts: BlogPostMetadata[];
};

const metadata: MetadataFile = metadataJson as MetadataFile;

const showAllPosts = import.meta.env.VITE_SHOW_ALL_POSTS === 'true';

export function isPublished(post: BlogPostMetadata): boolean {
  if (showAllPosts) return true;
  const publishedAt = new Date(post.date).getTime();
  if (Number.isNaN(publishedAt)) {
    console.error(`Invalid date for blog post ${post.slug}: ${post.date}`);
    return false;
  }
  return publishedAt <= Date.now();
}

const mdModules = import.meta.glob('/blogs/**/*.md', {
  query: '?raw',
  import: 'default',
});

function resolveLocale(locale: string): SupportedLocale {
  if (locale === 'en' || locale === 'zh-TW' || locale === 'zh-CN') {
    return locale;
  }
  return 'en';
}

function toLocalizedPost(post: BlogPostMetadata, locale: SupportedLocale): BlogPost {
  const localeData = post.locales[locale] ?? post.locales.en;
  return {
    slug: post.slug,
    date: post.date,
    tags: post.tags,
    coverImage: post.coverImage,
    featured: post.featured,
    title: localeData.title,
    excerpt: localeData.excerpt,
  };
}

function sortByDateDesc(a: BlogPost, b: BlogPost): number {
  return new Date(b.date).getTime() - new Date(a.date).getTime();
}

export function getBlogPosts(locale: string): BlogPost[] {
  const resolved = resolveLocale(locale);
  return metadata.posts
    .filter(isPublished)
    .map((post) => toLocalizedPost(post, resolved))
    .sort(sortByDateDesc);
}

export function getFeaturedPost(locale: string): BlogPost | null {
  const resolved = resolveLocale(locale);
  const featured = metadata.posts.find((p) => p.featured && isPublished(p));
  if (featured) return toLocalizedPost(featured, resolved);
  // Fallback to latest post
  const posts = getBlogPosts(locale);
  return posts[0] ?? null;
}

export function getLatestPosts(locale: string, limit: number): BlogPost[] {
  const resolved = resolveLocale(locale);
  const featuredSlug = metadata.posts.find((p) => p.featured && isPublished(p))?.slug;
  return metadata.posts
    .filter((p) => isPublished(p) && p.slug !== featuredSlug)
    .map((post) => toLocalizedPost(post, resolved))
    .sort(sortByDateDesc)
    .slice(0, limit);
}

export function getPostBySlug(locale: string, slug: string): BlogPost | null {
  const resolved = resolveLocale(locale);
  const post = metadata.posts.find((p) => p.slug === slug && isPublished(p));
  if (!post) return null;
  return toLocalizedPost(post, resolved);
}

export async function loadPostContent(locale: string, slug: string): Promise<string | null> {
  const resolved = resolveLocale(locale);
  const key = `/blogs/${resolved}/${slug}.md`;
  const loader = mdModules[key];
  if (!loader) return null;
  try {
    const content = (await loader()) as string;
    // Strip leading h1 heading since the page/modal already renders the title from metadata
    return content.replace(/^#\s+.+\n+/, '');
  } catch (error) {
    // A dynamic import() can reject at runtime (e.g. an old client fetching a
    // now-missing hashed chunk after a redeploy). Return null so callers hit the
    // existing not-found path instead of hanging on a spinner forever.
    console.error(`Failed to load blog content for ${resolved}/${slug}:`, error);
    return null;
  }
}

export function getRelatedPosts(locale: string, currentSlug: string, limit: number): BlogPost[] {
  const resolved = resolveLocale(locale);
  const current = metadata.posts.find((p) => p.slug === currentSlug);
  if (!current) return [];

  const currentTags = new Set(current.tags);
  const others = metadata.posts
    .filter((p) => p.slug !== currentSlug && isPublished(p))
    .map((post) => ({
      post,
      score: post.tags.filter((tag) => currentTags.has(tag)).length,
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ post }) => toLocalizedPost(post, resolved));

  return others;
}

export function getPostAvailableLocales(slug: string): SupportedLocale[] {
  const locales: SupportedLocale[] = ['en', 'zh-TW', 'zh-CN'];
  return locales.filter((loc) => {
    const key = `/blogs/${loc}/${slug}.md`;
    return key in mdModules;
  });
}

export function estimateReadingTime(content: string): number {
  const wordCount = content.split(/\s+/).length;
  return Math.max(1, Math.ceil(wordCount / 200));
}
